// `(donut …)` tile op — part-to-whole composition as a donut (inner-radius > 0).
//
// `(donut :category (col …) :value (col …) :span [W H] :format? :labels?
//         :legend? :center? (col …) :center-label? :colors? {…} :max-slices? N)`
//
// One row of the resolved dataset per slice: `:category` names the slice,
// `:value` sizes it. Registered in `dashboardMeta` (NOT `opMeta`) — only
// reachable as a child of `(dashboard …)`.

import { z } from 'zod';

import { categoryColorsArg, colRefArg, spanArg } from '../args.ts';
import { buildSpec, type DashboardCtx, dashboardMeta } from '../dispatch.ts';
import { DONUT_LABELS, METRIC_FORMATS } from '../enums.ts';
import type { DonutSpec } from '../value.ts';

export const donut = z
  .object({
    name: z.literal('donut'),
    params: z.tuple([]),
    span: spanArg,
    category: colRefArg,
    value: colRefArg,
    format: z.enum(METRIC_FORMATS).default('integer'),
    labels: z.enum(DONUT_LABELS).default('percent'),
    legend: z.boolean().default(true),
    center: colRefArg.optional(),
    'center-label': z.string().min(1).optional(),
    colors: categoryColorsArg.optional(),
    'max-slices': z.number().int().min(1).optional(),
  })
  .describe(
    'A donut chart for part-to-whole composition. One row per slice: `:category` names the slice, `:value` ' +
      'sizes it. `:format` formats slice/center numbers; `:labels` (default percent); ' +
      '`:legend` (default true). Optional center KPI: `:center` (col …) scalar, or the auto-summed slice ' +
      'total when only `:center-label` is set. `:colors {:category token …}` overrides per-category colors; ' +
      '`:max-slices N` collapses the smallest slices past the top N into one "Other" slice.',
  )
  .register(dashboardMeta, {
    evaluation: 'special',
    effect: 'pure',
    example: {
      expr: '(donut :category (col mix status) :value (col mix n) :center-label "Wells" :format integer :span [4 4])',
    },
    apply: (data, ctx: DashboardCtx): Promise<DonutSpec> =>
      buildSpec('donut-spec', data, ctx, [
        { key: 'span', mode: 'copy' },
        { key: 'category', mode: 'col' },
        { key: 'value', mode: 'col' },
        { key: 'format', mode: 'copy' },
        { key: 'labels', mode: 'copy' },
        { key: 'legend', mode: 'copy' },
        { key: 'center', mode: 'optional-col' },
        { key: 'centerLabel', from: 'center-label', mode: 'optional' },
        { key: 'colors', mode: 'optional' },
        { key: 'maxSlices', from: 'max-slices', mode: 'optional' },
      ]),
  });
